// searchModel: the row shape the host projects from the Library
// (searchProjection.ts) for every search surface: the Search page
// (SearchPage/searchRows.ts), the Studio and the resource views. This module
// owns NO display strings: closed enums and a `Recency` sum; the view
// localizes at the render edge.

import type { MaterialTemplate } from "../../app/ports.js";
import type { SourceReadStatus } from "../../app/sourceReadStatus.js";
import type { ExportFormatKind } from "../ExportView/ExportView.js";

// Freshness bucket — computed from a resource's `lastEditedAt` against the wall
// clock (searchProjection.freshnessOf), NEVER inside this module.
export type FreshnessBucket = "today" | "yesterday" | "this-week" | "earlier";

// A structured relative-time value — a sum, not a pre-baked string, so the
// component renders it in the viewer's locale. `raw` carries an upstream label
// (a conversation's `when`), passed through untouched.
export type Recency =
    | { readonly type: "now" }
    | { readonly type: "minutes"; readonly n: number }
    | { readonly type: "hours"; readonly n: number }
    | { readonly type: "yesterday" }
    | { readonly type: "days"; readonly n: number }
    | { readonly type: "date"; readonly day: number; readonly month: number } // month 0-11
    | { readonly type: "raw"; readonly text: string }
    | { readonly type: "unknown" };

// The five searchable entity kinds — the workspace's flat pool. A closed union;
// a new kind widens it and every match site breaks until it is placed.
export type SearchResultKind =
    | "source"
    | "material"
    | "package"
    | "file"
    | "conversation";

const SEARCH_KINDS: readonly SearchResultKind[] = [
    "source",
    "material",
    "package",
    "file",
    "conversation",
];

// One searchable entity, already projected from the Library. `meta` is a single
// precomputed supporting fragment (the format tag); `location` is the workspace.
export type SearchResult = {
    readonly kind: SearchResultKind;
    readonly id: string;
    readonly title: string;
    readonly location: string;
    readonly recency: Recency;
    readonly freshnessBucket: FreshnessBucket;
    readonly meta: string;
    // F86: the resource is archived (set aside). Absent ⇒ active.
    readonly archived?: true;
    // A source's "authors · year" (e.g. "Bird & de Moor · 1997").
    readonly byline?: string;
    // A source's full author names (author search).
    readonly authors?: readonly string[];
    // Creation moment, ISO 8601.
    readonly createdAt?: string;
    // How many live materials derive from it (the row's "i N material").
    readonly usedIn?: number;
    // Last touched, ISO 8601 (the "Edited" column and its sort).
    readonly editedAt?: string;
    readonly facts?: RowFacts;
    // A material the agent authored (FR-2.7): the row shows the AI mark.
    readonly aiAuthored?: true;
    // A source still being read in, or that failed to read (FR-3.3/3.6).
    // Absent once it is readable.
    readonly readStatus?: SourceReadStatus;
};

// What a material or package row says it is beyond its kind, so a material
// and the package made from it never read as duplicates. Data, localized by
// the view: a material's template, sources and versions; a package's format
// and the material it was made from.
type MaterialFacts = {
    readonly type: "material";
    readonly template?: MaterialTemplate;
    readonly basedOn: readonly string[];
    readonly versions?: number;
};
type PackageFacts = {
    readonly type: "package";
    readonly format: ExportFormatKind;
    readonly from?: string;
};
export type RowFacts = MaterialFacts | PackageFacts;

export type SearchFacet = SearchResultKind;
export const SEARCH_FACETS: readonly SearchFacet[] = SEARCH_KINDS;

export type TextSegment = { readonly text: string; readonly matched: boolean };
